import { useState, useEffect, useRef, useCallback } from "react"
import { motion, AnimatePresence } from "motion/react"
import { Music, Pause, Volume2, VolumeX } from "lucide-react"
import gramophoneImg from "@/assets/oval-tunes-gramophone.png"
import { useAudioAnalyser } from "@/hooks/useAudioAnalyser"

declare global {
  interface Window {
    YT: any
    onYouTubeIframeAPIReady: () => void
  }
}

const YOUTUBE_VIDEO_ID = "BfnjX88Va4Y"

export function MusicPlayer() {
  const [isPlaying, setIsPlaying] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [isMuted, setIsMuted] = useState(true)
  const playerRef = useRef<any>(null)
  
  // Get amplitude for gramophone pulse animation
  const amplitude = useAudioAnalyser(isPlaying)

  // Load YouTube IFrame API
  useEffect(() => {
    if (window.YT && window.YT.Player) {
      createPlayer()
      return
    }

    const tag = document.createElement("script")
    tag.src = "https://www.youtube.com/iframe_api"
    const firstScript = document.getElementsByTagName("script")[0]
    firstScript?.parentNode?.insertBefore(tag, firstScript)

    window.onYouTubeIframeAPIReady = () => {
      createPlayer()
    }

    return () => {
      if (playerRef.current?.destroy) {
        playerRef.current.destroy()
      }
    }
  }, [])

  const createPlayer = useCallback(() => {
    if (playerRef.current) return

    playerRef.current = new window.YT.Player("yt-music-player", {
      height: "0",
      width: "0",
      videoId: YOUTUBE_VIDEO_ID,
      playerVars: {
        autoplay: 0,
        controls: 0,
        disablekb: 1,
        fs: 0,
        modestbranding: 1,
        playsinline: 1,
        rel: 0,
        loop: 1,
        playlist: YOUTUBE_VIDEO_ID,
      },
      events: {
        onReady: () => {
          setIsReady(true)
          playerRef.current?.setVolume(50)
          // Autoplay muted (browsers require mute for autoplay)
          playerRef.current?.mute()
          playerRef.current?.playVideo()
        },
        onStateChange: (event: any) => {
          if (event.data === 1) {
            setIsPlaying(true)
          } else if (event.data === 2 || event.data === 0) {
            setIsPlaying(false)
          }
        },
      },
    })
  }, [])

  const togglePlay = () => {
    if (!playerRef.current || !isReady) return
    if (isPlaying) {
      playerRef.current.pauseVideo()
    } else {
      playerRef.current.playVideo()
    }
  }

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!playerRef.current || !isReady) return
    if (isMuted) {
      playerRef.current.unMute()
      setIsMuted(false)
    } else {
      playerRef.current.mute()
      setIsMuted(true)
    }
  }

  return (
    <div className="relative">
      {/* Hidden YouTube Player */}
      <div style={{ position: "fixed", top: -9999, left: -9999, width: 0, height: 0, overflow: "hidden", pointerEvents: "none" }}>
        <div id="yt-music-player" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: "spring", bounce: 0.25, duration: 0.5 }}
        className="bg-[#1C1C32] rounded-2xl border-[2.5px] border-[#2A2A45] shadow-[0_0_8px_rgba(243,112,32,0.12)] overflow-hidden"
      >
        <div className="p-2.5 flex items-center gap-2.5">
          {/* Gramophone image */}
          <motion.button
            onClick={togglePlay}
            className="relative w-11 h-11 cursor-pointer focus:outline-none flex-shrink-0 group"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.92 }}
            aria-label={isPlaying ? "Pause music" : "Play music"}
          >
            {/* Glow effect behind gramophone */}
            <div
              className="absolute -inset-1.5 rounded-full opacity-100"
              style={{ background: "radial-gradient(circle, rgba(243,112,32,0.4) 0%, rgba(255,215,0,0.15) 60%, transparent 80%)" }}
            />

            {/* Gramophone image with pulse animation */}
            <motion.img
              src={gramophoneImg}
              alt="Gramophone"
              className="w-full h-full object-contain relative z-10"
              style={{ filter: "drop-shadow(2px 2px 0 #1a1a1a)", willChange: "transform" }}
              animate={{ scale: 1 + amplitude * 0.08 }}
              transition={{ duration: 0.1, ease: "easeOut" }}
            />

            {/* Play icon overlay when not playing */}
            <AnimatePresence mode="wait">
              {!isPlaying && (
                <motion.div
                  key="play"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.5 }}
                  className="absolute inset-0 rounded-full flex items-center justify-center bg-black/30 z-20"
                >
                  <Music size={16} className="text-white drop-shadow-lg" />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Pause on hover when playing */}
            {isPlaying && (
              <div className="absolute inset-0 rounded-full flex items-center justify-center bg-black/0 hover:bg-black/40 transition-colors duration-200 group/pause z-20">
                <Pause size={14} className="text-white opacity-0 group-hover/pause:opacity-100 transition-opacity duration-200 drop-shadow-lg" />
              </div>
            )}
          </motion.button>

          {/* Info & controls */}
          <div className="flex-1 min-w-0">
            {/* Status line */}
            <AnimatePresence mode="wait">
              {isPlaying ? (
                <motion.div
                  key="playing"
                  initial={{ opacity: 0, y: 3 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -3 }}
                  className="flex items-center gap-1.5"
                >
                  <div className="flex items-end gap-[2px] h-2.5">
                    {[0, 1, 2, 3].map((i) => (
                      <motion.div
                        key={i}
                        className="w-[2.5px] rounded-full"
                        style={{ background: "linear-gradient(to top, #F37020, #FFD700)" }}
                        animate={{ height: ["3px", "10px", "5px", "8px", "3px"] }}
                        transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.15, ease: "easeInOut" }}
                      />
                    ))}
                  </div>
                  <span
                    className="text-[9px] font-bold text-[#F37020] uppercase tracking-wider"
                    style={{ fontFamily: "Outfit, -apple-system, BlinkMacSystemFont, sans-serif" }}
                  >
                    Now Playing
                  </span>
                </motion.div>
              ) : (
                <motion.div
                  key="idle"
                  initial={{ opacity: 0, y: 3 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -3 }}
                >
                  <span
                    className="text-[9px] font-bold text-[#6A6A8A] uppercase tracking-wider"
                    style={{ fontFamily: "Outfit, -apple-system, BlinkMacSystemFont, sans-serif" }}
                  >
                    Tap to vibe
                  </span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Track name */}
            <p
              className="text-[11px] font-bold text-white truncate mt-0.5 leading-tight"
              style={{ fontFamily: "Outfit, -apple-system, BlinkMacSystemFont, sans-serif" }}
            >
              Oval Vibes Radio
            </p>

            {/* Controls */}
            <div className="flex items-center gap-1 mt-1.5">
              <motion.button
                onClick={togglePlay}
                className="flex items-center justify-center w-6 h-6 rounded-lg border-2 border-[#2A2A45]/60 bg-[#1A1A30] hover:bg-[#F37020]/10 hover:border-[#F37020]/40 transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                aria-label={isPlaying ? "Pause" : "Play"}
              >
                {isPlaying ? (
                  <Pause size={11} className="text-[#9A9ABF]" />
                ) : (
                  <Music size={11} className="text-[#9A9ABF]" />
                )}
              </motion.button>

              <motion.button
                onClick={toggleMute}
                className="flex items-center justify-center w-6 h-6 rounded-lg border-2 border-[#2A2A45]/60 bg-[#1A1A30] hover:bg-[#F37020]/10 hover:border-[#F37020]/40 transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                aria-label={isMuted ? "Unmute" : "Mute"}
              >
                {isMuted ? (
                  <VolumeX size={11} className="text-[#9A9ABF]" />
                ) : (
                  <Volume2 size={11} className="text-[#9A9ABF]" />
                )}
              </motion.button>

              {/* Status dot */}
              <div className="ml-auto">
                <motion.div
                  className="w-1.5 h-1.5 rounded-full"
                  style={{
                    background: isPlaying ? "#32CD32" : "#ccc",
                    boxShadow: isPlaying ? "0 0 6px rgba(50,205,50,0.5)" : "none",
                  }}
                  animate={isPlaying ? { opacity: [1, 0.4, 1] } : {}}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                />
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}
